/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getStylist } from 'src/core/themes/luisStylist';

const { getClassName } = getStylist('ErrorBoundary');
const textAlignClassName = getClassName('errorBoundaryTextAlign', { textAlign: 'center' });

interface IProps {
    children: React.ReactNode;
}

/**
 * A component that catches any Javascript error in a more
 * "graceful manner".
 */
export class ErrorBoundary extends React.Component<IProps, { hasError: boolean }> {
    constructor(props: Readonly<IProps>) {
        super(props);
        this.state = { hasError: false };
    }

    /**
     * Catches any JavaScript related errors that might
     * crash the website.
     */
    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        getEventLogger('ErrorBoundary').error<'DefaultErrorLogEvent'>(error, { errorInfo });
        this.setState({ hasError: true });
    }

    /**
     * Renders the component
     */
    public render() {
        if (this.state.hasError) {
            return (
                <Page pageName="ErrorBoundaryPage">
                    <Stack horizontalAlign="center" verticalAlign="center" verticalFill tokens={{ childrenGap: 20 }}>
                        <LuisText fontWeight="normal" variant="xxLarge" className={textAlignClassName}>
                            Something went wrong. Please refresh the page to resume usage.
                        </LuisText>
                    </Stack>
                </Page>
            );
        }

        return this.props.children;
    }
}
